import React, { useEffect, useReducer } from "react";

import { isEmpty } from "ramda";

import cdesApi from "apis/cdeDefinitions";
import dataQualityAnalysisApis from "apis/dataQualityAnalysis";
import domainsApi from "apis/domains";
import domainsExploreApi from "apis/domainsExplore";
import {
  Button,
  PageLoader,
  Select,
  SubHeader,
  Typography,
} from "clearsense-ui/components";
import useAutoCollapse from "hooks/useAutoCollapse";
import { initialState, reducer, QA_ACTIONS } from "reducers/dataQaReducer";

import DataOccurrence from "./DataOccurrence";
import DataTable from "./DataTable";
import RulesStatistics from "./RulesStatistics";

const DataQaAnalysis = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchCdes = async () => {
    try {
      dispatch({ type: QA_ACTIONS.CDE_LOADING, payload: true });
      const { data } =
        state?.selectedDomain?.label === "All"
          ? await cdesApi.fetchCdes("", 1, 100)
          : await domainsExploreApi.fetchCdes(
              state?.selectedDomain?.value,
              1,
              100,
              ""
            );
      const cdeData = data.cde_definitions.cdes.map(cde => ({
        value: cde.id,
        label: cde.name,
      }));
      dispatch({ type: QA_ACTIONS.CDES, payload: cdeData });
    } catch (error) {
      logger.log(error);
    }
  };

  const fetchTableData = async (page, per) => {
    try {
      dispatch({ type: QA_ACTIONS.SET_IS_TABLE_LOADING, payload: true });
      const { data } = await dataQualityAnalysisApis.table_view(
        state?.selectedCde?.value,
        page,
        per
      );
      dispatch({
        type: QA_ACTIONS.SET_TABLE_DATA,
        payload: data?.quality_analysis?.table_data,
      });
      dispatch({
        type: QA_ACTIONS.SET_TOTAL_RECORDS_COUNT,
        payload: data?.quality_analysis?.count,
      });
    } catch (error) {
      logger.log(error);
    } finally {
      dispatch({ type: QA_ACTIONS.SET_IS_TABLE_LOADING, payload: false });
    }
  };

  const fetchRulesMetrics = async () => {
    try {
      const { data } = await dataQualityAnalysisApis.quality_rules_metrics(
        state?.selectedCde?.value
      );
      dispatch({
        type: QA_ACTIONS.QA_METRICS,
        payload: data?.quality_rules_metrics,
      });
    } catch (error) {
      logger.log(error);
    }
  };

  const fetchDomains = async () => {
    try {
      dispatch({ type: QA_ACTIONS.DOMAIN_LOADING, payload: true });
      const { data } = await domainsApi.fetch("", 1, 1000);
      const domainsData = [
        { label: "All", value: "All" },
        ...data.domains.domain_data.map(domain => ({
          label: domain.name,
          value: domain.id,
        })),
      ];
      dispatch({ type: QA_ACTIONS.DOMAINS, payload: domainsData });
    } catch (error) {
      logger.log(error);
    }
  };

  const setPage = page =>
    dispatch({ type: QA_ACTIONS.SET_PAGE, payload: page });

  const setPer = per =>
    dispatch({ type: QA_ACTIONS.SET_RESULTS_PER_PAGE, payload: per });

  const handleSearch = async () => {
    dispatch({ type: QA_ACTIONS.SET_ALL_LOADING });
    try {
      const { data } = await dataQualityAnalysisApis.analysis(
        state?.selectedCde?.value
      );
      const dsdeValues = data.quality_analysis.map(item => ({
        name: Object.values(item)[0],
        key: Object.keys(item)[0],
        count: item["Number of Occurances"],
      }));
      fetchRulesMetrics();
      dispatch({ type: QA_ACTIONS.DSDE_VALUES, payload: dsdeValues });
      setPage(1);
      setPer(10);
      fetchTableData(state.page, state.per);
    } catch (error) {
      logger.info(error);
    } finally {
      dispatch({
        type: QA_ACTIONS.TITLE,
        payload: {
          domain: state.selectedDomain.label,
          cde: state.selectedCde.label,
        },
      });
      dispatch({ type: QA_ACTIONS.CLEAR_ALL_LOADING });
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    dispatch({ type: QA_ACTIONS.SELECTED_CDE, payload: null });
    state?.selectedDomain?.value && fetchCdes();
  }, [state?.selectedDomain?.value]);

  useEffect(() => {
    state?.selectedCde && fetchTableData(state.page, state.per);
  }, [state.page, state.per]);

  useAutoCollapse();

  return (
    <>
      <SubHeader
        breadcrumbs={[{ label: "Data Quality Analysis", link: "#" }]}
        className="p-4"
      />
      <div className="flex gap-2 m-4">
        <Select
          size="small"
          label=" Select Domain"
          options={state.domains}
          name="domainId"
          onChange={e => {
            dispatch({ type: QA_ACTIONS.SELECTED_DOMAIN, payload: e });
          }}
          required
          isDisabled={
            state.isDomainLoading || state.isSearching || isEmpty(state.domains)
          }
          isLoading={state.isDomainLoading}
        />
        <Select
          size="small"
          label=" Select CDE"
          options={state.cdes}
          name="cdeId"
          value={state?.selectedCde}
          onChange={e =>
            dispatch({ type: QA_ACTIONS.SELECTED_CDE, payload: e })
          }
          required
          isDisabled={
            state.isDomainLoading ||
            state.isSearching ||
            !state?.selectedDomain?.value ||
            isEmpty(state.cdes)
          }
          isLoading={state.isCDELoading}
        />
        <div className="self-end">
          <Button
            type="submit"
            label="Search"
            disabled={state.isSearching || !state?.selectedCde?.value}
            loading={state.isSearching}
            onClick={handleSearch}
          />
        </div>
      </div>
      {state.isSearching ? (
        <div className="h-full w-full grid place-items-center">
          <PageLoader />
        </div>
      ) : (
        <>
          {state.dsdeValues && state.dsdeValues.length > 0 ? (
            <div className="data_qa_dashboard__results p-4">
              <Typography style="h3" type="semibold">
                Data Quality Analysis of {state?.title?.cde} CDE
                {state?.title?.domain !== "All" &&
                  ` under ${state?.title?.domain} Domain`}
              </Typography>
              <div className="border-b border-gray-500 border-solid my-2 opacity-40" />
              <DataOccurrence chartData={state.dsdeValues} />
              {state.qaMetrics?.length > 0 && (
                <RulesStatistics chartData={state.qaMetrics} />
              )}
              {state.tableData ? (
                <DataTable
                  tableData={state.tableData}
                  isLoading={state.isTableLoading}
                  page={state.page}
                  setPage={setPage}
                  per={state.per}
                  setPer={setPer}
                  totalRecordsCount={state.totalRecordsCount}
                />
              ) : (
                <PageLoader text="Loading table." />
              )}
            </div>
          ) : (
            <div className="w-full justify-center flex">
              {state?.selectedCde && state.dsdeValues?.length === 0 && (
                <Typography style="h3" type="semibold">
                  No data available
                </Typography>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default DataQaAnalysis;
